import { createFileRoute } from "@tanstack/react-router";
import previewAsset from "@/assets/preview-vertex-snow.jpg.asset.json";
import heroAsset from "@/assets/vertex-snow-hero.jpg.asset.json";
import board1 from "@/assets/vertex-board-1.jpg.asset.json";
import board2 from "@/assets/vertex-board-2.jpg.asset.json";
import board3 from "@/assets/vertex-board-3.jpg.asset.json";

export const Route = createFileRoute("/demo/vertex-snow")({
  head: () => ({
    meta: [
      { title: "VERTEX Snow — Premium Snowboards Swiss Made" },
      { name: "description", content: "Snowboard premium, boots e attrezzatura su misura. Engineered in Switzerland. Riding senza compromessi." },
      { name: "robots", content: "noindex,nofollow" },
      { property: "og:title", content: "VERTEX Snow — Premium Snowboards Swiss Made" },
      { property: "og:description", content: "Snowboard premium, boots e attrezzatura su misura. Engineered in Switzerland." },
      { property: "og:image", content: previewAsset.url },
    ],
    links: [
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Readex+Pro:wght@300;400;500;600;700&display=swap",
      },
    ],
  }),
  component: VertexSnowDemo,
});

const FONT = "'Readex Pro', system-ui, -apple-system, sans-serif";

function Logo({ className = "h-5 w-5" }: { className?: string }) {
  return (
    <svg viewBox="0 0 256 256" className={className} aria-hidden>
      <path
        fill="#ffffff"
        d="M 128 192 L 128 256 L 64.5 256 L 32 223 L 0 192 L 0 128 L 64 128 Z M 256 192 L 256 256 L 192.5 256 L 160 223 L 128 192 L 128 128 L 192 128 Z M 128 64 L 128 128 L 64.5 128 L 32 95 L 0 64 L 0 0 L 64 0 Z M 256 64 L 256 128 L 192.5 128 L 160 95 L 128 64 L 128 0 L 192 0 Z"
      />
    </svg>
  );
}

function VertexSnowDemo() {
  return (
    <div style={{ fontFamily: FONT, background: "#000", color: "#fff" }} className="min-h-screen antialiased overflow-x-hidden">
      <style>{`
        .hero-title { letter-spacing: -0.04em; line-height: 0.95; }
      `}</style>

      {/* HERO */}
      <section className="relative h-screen w-full overflow-hidden bg-black">
        <video
          className="absolute inset-0 w-full h-full object-cover"
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
        >
          <source
            src="https://d8j0ntlcm91z4.cloudfront.net/user_38xzZboKViGWJOttwIXH07lWA1P/hf_20260418_063509_7d167302-4fd4-480b-8260-18ab572333d4.mp4"
            type="video/mp4"
          />
        </video>
        <div className="absolute inset-0 bg-black/30" />

        {/* Navbar */}
        <div className="absolute top-0 left-0 right-0 z-20 px-6 md:px-10 pt-6">
          <nav className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2 bg-neutral-900/90 backdrop-blur rounded-full pl-4 pr-6 py-3">
              <Logo />
              <span className="text-white text-sm font-normal tracking-tight lowercase">vertex</span>
            </div>
            <div className="hidden md:flex items-center gap-1 bg-neutral-900/90 backdrop-blur rounded-full px-3 py-2">
              {["boards", "boots", "team", "support"].map((l) => (
                <a key={l} href="#" className="text-neutral-300 hover:text-white transition-colors text-sm px-5 py-2 rounded-full lowercase">
                  {l}
                </a>
              ))}
            </div>
            <a
              href="#shop"
              className="bg-white text-black text-sm font-normal rounded-full px-6 py-3 hover:bg-neutral-200 transition-colors lowercase"
            >
              shop now
            </a>
          </nav>
        </div>

        {/* Foreground */}
        <div className="relative h-full w-full">
          <h1 className="hero-title absolute left-4 md:left-10 top-[18%] text-white font-medium text-[14vw] md:text-[13vw] lowercase">ride</h1>
          <h1 className="hero-title absolute right-4 md:right-10 top-[38%] text-white font-medium text-[14vw] md:text-[13vw] lowercase">the</h1>
          <h1 className="hero-title absolute left-[18%] md:left-[28%] top-[58%] text-white font-medium text-[14vw] md:text-[13vw] lowercase">storm</h1>

          <p className="absolute left-6 md:left-10 top-[46%] max-w-[240px] text-[15px] leading-snug text-white/90 lowercase">
            snowboard premium scolpite a mano in svizzera, per chi non si accontenta della pista battuta.
          </p>

          {/* top-right stat */}
          <div className="absolute right-6 md:right-24 top-[14%]">
            <div className="flex items-center gap-3 justify-end">
              <span className="hidden md:block h-px w-24 bg-white/40 rotate-[20deg]" />
              <span className="text-4xl md:text-5xl font-medium tracking-tight lowercase">+42k</span>
            </div>
            <div className="text-xs md:text-sm text-white/70 mt-1 text-right lowercase">riders nel mondo</div>
          </div>

          {/* bottom-left stat */}
          <div className="absolute left-6 md:left-20 bottom-20 md:bottom-24">
            <div className="flex items-center gap-3">
              <span className="text-4xl md:text-5xl font-medium tracking-tight lowercase">+18</span>
              <span className="hidden md:block h-px w-24 bg-white/40 rotate-[-20deg]" />
            </div>
            <div className="text-xs md:text-sm text-white/70 mt-1 lowercase">stagioni di ricerca</div>
          </div>

          {/* bottom-right stat */}
          <div className="absolute right-6 md:right-20 bottom-16 md:bottom-20">
            <div className="flex items-center gap-3 justify-end">
              <span className="hidden md:block h-px w-24 bg-white/40 rotate-[-20deg]" />
              <span className="text-4xl md:text-5xl font-medium tracking-tight lowercase">+300</span>
            </div>
            <div className="text-xs md:text-sm text-white/70 mt-1 text-right lowercase">shop partner</div>
          </div>

          {/* bottom gradient */}
          <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-48 bg-gradient-to-b from-transparent to-black" />
        </div>
      </section>

      {/* PRODUCTS */}
      <section id="shop" className="relative bg-black py-24 px-6 md:px-10">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <div className="text-white/50 text-xs uppercase tracking-[0.3em] mb-3">collezione 25/26</div>
              <h2 className="hero-title text-5xl md:text-7xl font-medium lowercase">le tavole</h2>
            </div>
            <p className="text-white/70 max-w-md lowercase">
              tre serie, una filosofia: legno di pioppo svizzero, lamine in acciaio rockwell 48, sidecut radiale per ogni terreno.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { img: board1.url, name: "apex 159", style: "all-mountain", price: "CHF 690" },
              { img: board2.url, name: "glacier 156", style: "freeride / powder", price: "CHF 820" },
              { img: board3.url, name: "voltage 152", style: "freestyle / park", price: "CHF 590" },
            ].map((p) => (
              <article key={p.name} className="group relative bg-neutral-950 border border-white/10 rounded-3xl overflow-hidden hover:border-white/30 transition-colors">
                <div className="aspect-[3/4] overflow-hidden bg-black flex items-center justify-center">
                  <img
                    src={p.img}
                    alt={p.name}
                    width={800}
                    height={1200}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-6 flex items-center justify-between">
                  <div>
                    <div className="text-white text-lg lowercase">{p.name}</div>
                    <div className="text-white/50 text-sm lowercase">{p.style}</div>
                  </div>
                  <div className="text-white font-medium lowercase">{p.price}</div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* TECH SPECS */}
      <section className="relative bg-neutral-950 py-24 px-6 md:px-10 border-t border-white/5">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-16 items-center">
          <div>
            <div className="text-white/50 text-xs uppercase tracking-[0.3em] mb-3">tecnologia</div>
            <h2 className="hero-title text-4xl md:text-6xl font-medium lowercase mb-6">engineered in andermatt.</h2>
            <p className="text-white/70 leading-relaxed lowercase mb-8">
              ogni tavola nasce nel nostro atelier alpino. testate su 6 montagne svizzere prima di entrare in produzione. zero compromessi sulla qualità del riding.
            </p>
            <a href="#" className="inline-block bg-white text-black text-sm rounded-full px-6 py-3 hover:bg-neutral-200 transition-colors lowercase">
              scopri il processo
            </a>
          </div>

          <div className="grid grid-cols-2 gap-4">
            {[
              { k: "rocker", v: "ibrido camber/rocker" },
              { k: "core", v: "pioppo + bamboo" },
              { k: "lamine", v: "acciaio 48 hrc" },
              { k: "base", v: "sintered 7200" },
              { k: "flex", v: "5 / 10 medium" },
              { k: "peso", v: "2.8 kg / 156cm" },
            ].map((s) => (
              <div key={s.k} className="border border-white/10 rounded-2xl p-5 bg-black/50">
                <div className="text-white/50 text-xs uppercase tracking-widest">{s.k}</div>
                <div className="text-white mt-2 lowercase">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* RIDERS / TESTIMONIALS */}
      <section className="relative bg-black py-24 px-6 md:px-10 border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <div>
              <div className="text-white/50 text-xs uppercase tracking-[0.3em] mb-3">il team</div>
              <h2 className="hero-title text-4xl md:text-6xl font-medium lowercase">chi ride vertex.</h2>
            </div>
            <p className="text-white/70 max-w-md lowercase">
              pro rider, freeride coach e tester che vivono la montagna 200 giorni all'anno. il loro feedback entra in ogni tavola.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { q: "la apex 159 è la tavola più equilibrata che abbia mai testato. mi porta dal park alla powder senza pensarci.", n: "elias rüegg", r: "freeride world tour qualifier" },
              { q: "lavoriamo con vertex da 4 stagioni. costruzione svizzera, rocker preciso, sidecut perfetto per la nostra scuola di andermatt.", n: "anna keller", r: "head coach, snowschool gemsstock" },
              { q: "ho fatto 80 giorni sulla glacier 156. zero delaminazioni, base che ancora va come il primo giorno.", n: "marco bonetti", r: "splitboard guide, valle bedretto" },
            ].map((t, i) => (
              <figure key={i} className="bg-neutral-950 border border-white/10 rounded-3xl p-7 flex flex-col justify-between">
                <blockquote className="text-white/90 leading-relaxed lowercase">"{t.q}"</blockquote>
                <figcaption className="mt-6 pt-5 border-t border-white/10">
                  <div className="text-white text-sm lowercase">{t.n}</div>
                  <div className="text-white/50 text-xs lowercase mt-1">{t.r}</div>
                </figcaption>
              </figure>
            ))}
          </div>

          {/* press */}
          <div className="mt-20 border-t border-white/10 pt-12">
            <div className="text-white/50 text-xs uppercase tracking-[0.3em] mb-6 text-center">recensiti su</div>
            <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-4 text-white/55">
              <span className="text-lg lowercase">whitelines</span>
              <span className="text-white/15">·</span>
              <span className="text-lg italic lowercase">onboard mag</span>
              <span className="text-white/15">·</span>
              <span className="text-lg lowercase">snowboarder ch</span>
              <span className="text-white/15">·</span>
              <span className="text-lg italic lowercase">pleasure mag</span>
              <span className="text-white/15">·</span>
              <span className="text-lg lowercase">méthod mag</span>
            </div>
          </div>

          {/* trust stats */}
          <div className="mt-16 grid grid-cols-2 sm:grid-cols-4 gap-6 border-t border-white/10 pt-12">
            {[
              ["18", "stagioni r&d"],
              ["6", "test mountains"],
              ["300+", "shop partner ue"],
              ["2y", "garanzia core"],
            ].map(([n, l]) => (
              <div key={l}>
                <div className="hero-title text-4xl md:text-5xl font-medium text-white lowercase">{n}</div>
                <div className="text-white/50 text-xs uppercase tracking-widest mt-2">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}

      <section className="relative bg-black py-32 px-6 md:px-10 text-center">
        <h2 className="hero-title text-5xl md:text-8xl font-medium lowercase max-w-4xl mx-auto">
          la prossima discesa <span className="text-white/40">ti aspetta.</span>
        </h2>
        <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
          <a href="#shop" className="bg-white text-black rounded-full px-8 py-4 text-sm hover:bg-neutral-200 transition-colors lowercase">
            configura la tua tavola
          </a>
          <a href="#" className="border border-white/30 text-white rounded-full px-8 py-4 text-sm hover:bg-white/10 transition-colors lowercase">
            trova uno shop
          </a>
        </div>
        <div className="mt-16 text-white/40 text-xs lowercase">© vertex snow — swiss made. andermatt, ch.</div>
      </section>
    </div>
  );
}
